import { computed, Injectable, signal } from '@angular/core';

import { RETURN_METHODS } from '../models/return-packaging.constants';
import { IReturnPickupLocation } from '../models/i-return-pickup-location';
import { IReturnPackagingDetails, IReturnPackagingLine } from '../models/i-return-packaging-details';

/**Holds one customer's return choice while they make it. Provided by the step container, not in
 * root, so the selection lives and dies with the step.*/
@Injectable()
export class ReturnPackagingStore {
  readonly method = signal<RETURN_METHODS>(null);
  readonly date = signal<string>(null);
  readonly time = signal<string>(null);
  readonly outletId = signal<number>(null);
  readonly pickupAddress = signal<string>('');
  readonly pickupPostNumber = signal<string>('');
  readonly pickupCity = signal<string>('');
  readonly price = signal<number>(null);

  readonly isSelfReturn = computed(() => this.method() === RETURN_METHODS.SELF);
  readonly isCourierReturn = computed(() => this.method() === RETURN_METHODS.COURIER);

  readonly hasSchedule = computed(() => Boolean(this.date() && this.time()));

  readonly canSubmit = computed(() => {
    if (!this.method() || !this.hasSchedule()) {
      return false;
    }

    return this.isCourierReturn() ? Boolean(this.pickupPostNumber() && this.pickupAddress()) : Boolean(this.outletId());
  });

  readonly details = computed<IReturnPackagingDetails>(() => ({
    outlet: this.isSelfReturn() ? this.outletId() : null,
    returnMethod: this.method(),
    pickupAddress: this.isCourierReturn() ? this.pickupAddress() : '',
    pickupPostNumber: this.isCourierReturn() ? this.pickupPostNumber() : '',
    pickupCity: this.isCourierReturn() ? this.pickupCity() : '',
    scheduledDate: this.date(),
    scheduledTime: this.time(),
  }));

  /**Switching method invalidates everything schedule-shaped: the two methods read different calendars.*/
  public selectMethod(method: RETURN_METHODS): void {
    if (this.method() === method) {
      return;
    }

    this.method.set(method);
    this.clearSchedule();
  }

  public setPickupLocation({ postNumber, address, city }: IReturnPickupLocation): void {
    this.pickupPostNumber.set(postNumber || '');
    this.pickupAddress.set(address || '');
    this.pickupCity.set(city || '');
  }

  /**The schedule and its price were quoted for the location being cleared, so they go with it.*/
  public clearPickupLocation(): void {
    this.pickupPostNumber.set('');
    this.pickupCity.set('');
    this.clearSchedule();
  }

  public clearSchedule(): void {
    this.date.set(null);
    this.time.set(null);
    this.price.set(null);
  }

  public hydrateFrom(line: IReturnPackagingLine): void {
    const details = line && line.returnPackaging;

    if (!details) {
      return;
    }

    const { returnMethod, scheduledDate, scheduledTime, outlet, pickupAddress, pickupPostNumber, pickupCity } = details;

    this.method.set(returnMethod);
    this.date.set(scheduledDate);
    this.time.set(scheduledTime);
    this.outletId.set(outlet);
    this.pickupAddress.set(pickupAddress || '');
    this.pickupPostNumber.set(pickupPostNumber || '');
    this.pickupCity.set(pickupCity || '');
    this.price.set(line.price || null);
  }

  public reset(): void {
    this.method.set(null);
    this.outletId.set(null);
    this.pickupAddress.set('');
    this.pickupPostNumber.set('');
    this.pickupCity.set('');
    this.clearSchedule();
  }
}
